import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { WEB_DEVICE_TYPE } from '@core/constants';
import { HttpMessageCode, PhotoType } from '@core/enums';
import { SessionService } from '@core/services';
import { TranslateService } from '@ngx-translate/core';
import { ImageTransform } from '@ui/image-cropper';
import Bowser from 'bowser';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject, timer } from 'rxjs';

import { APP_CONFIG } from '../../environments/environment';

import { joinPublicUrl } from './join-public-url';
import { GET_METHOD, POST_METHOD } from './user-http-methods';

/** The shared state of the product's image cropper (`<image-cropper>` and the page image field). Typed for review MR !1 N30. */
export interface CropperState {
    imageName: string;
    canvasRotation: number;
    rotation: number;
    scale: number;
    showCropper: boolean;
    containWithinAspectRatio: boolean;
    aspectRatio: string;
    resizeToWidth: number;
    imageQuality: number;
    cropperMinWidth: number;
    onlyScaleDown: boolean;
    roundCropper: boolean;
    alignImage: string;
    format: string;
    transform: ImageTransform & { translateUnit?: string };
    /** In megabytes. */
    maxMb: { entity: number; hero: number; patient: number; registrationCertificate: number };
}

/** A profile saved on this browser, as the sign-in answers stored it (review MR !1 S39: the return type of getUserData). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the stored shape is whatever the sign-in API returned
export type StoredProfile = Record<string, any>;

/** The toast kinds `toastrWithTitle` shows. */
export enum ToastKind {
    Success = 'success',
    Error = 'error',
    Info = 'info',
    Warning = 'warning',
}

/**
 * The session, storage and request helper every sign-in and portal screen shares: the signed-in
 * user's identity in storage, the authenticated request wrappers, the picture helpers and the
 * cropper state.
 */
@Injectable({
    providedIn: 'root'
})
export class HelperService {
    trans = inject(TranslateService);
    http = inject(HttpClient);
    router = inject(Router);
    toastr = inject(ToastrService);
    private readonly sessionService = inject(SessionService);
    /** The root injector's: the timers below end with the app, never outlive it (review MR !1 S17). */
    private readonly destroyRef = inject(DestroyRef);

    public isHeaderPopupOpen = false;
    public userAgent: any = Bowser.parse(window.navigator.userAgent);
    public myLoading = false;
    public environment = APP_CONFIG;
    public GET_METHOD: any = GET_METHOD;
    public POST_METHOD: any = POST_METHOD;
    public cropValue: CropperState = {
        imageName: '',
        canvasRotation: 0,
        rotation: 0,
        scale: 1,
        showCropper: false,
        containWithinAspectRatio: false,
        aspectRatio: '4 / 4',
        resizeToWidth: 256,
        imageQuality: 100,
        cropperMinWidth: 128,
        onlyScaleDown: true,
        roundCropper: false,
        alignImage: 'left',
        format: 'png',
        transform: {
            translateUnit: 'px'
        },
        maxMb: { // in mb
            entity: 0.097,
            hero: 0.097,
            patient: 0.097,
            registrationCertificate: 2,
        }
    };
    public errorDisabled: any = false;
    public checkUserLength: any = true;
    public lastUrl: any = '/home';
    public tempStorage: any = {};
    public settingData: any = null;
    /**
   * TRUE ONCE THE PICTURE HOST IS KNOWN. `IMAGE_BASE_URL` starts as the build's default and is replaced by the settings' own
   * `s3_file_url` when the settings arrive. A page drawn before that asked the WRONG host for every picture - a broken-image icon and
   * the alt text for a moment on every refresh, then the real picture (owner, 2026-10-16). Screens that draw uploaded pictures wait
   * for this. It also turns true after 4 seconds, so a settings call that never answers cannot hide the pictures for good.
   */
    public readonly imageBaseReady$ = new BehaviorSubject<boolean>(false);
    get imageBaseReady(): boolean {
        return this.imageBaseReady$.value;
    }

    public otpExpireTime: any = 60;
    public otpValidTime: any = 5;
    public doctorName = 'Dr.';
    public phoneReg: any = new RegExp('^[0-9]{10}$');

    public linkedEntity: any = [];

    public isLoadImage: any = false;
    public activeRequests = 0;
    public readonly userDataUpdated$ = new Subject<void>();
    currentTime(): Date {
        return new Date();
    }

    constructor() {
        this.trans.use('en');
        this.router.events.subscribe((event: any) => {
            if (event instanceof NavigationEnd) {
                if (this.sessionService.isEndingSession) {
                    return;
                }
                this.toastr.clear();
            }
        });

        // RxJS timers, not setTimeout (review MR !1 S17). Same delays: settings after 1 s, the picture host assumed after 4 s.
        timer(4000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.imageBaseReady$.next(true));
        timer(1000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.http_get_method_requester(
                this.GET_METHOD.GET_SETTINGS, '',
                false, false, false, false, (res_data: any) => {
                    if (res_data.success == true) {

                        this.settingData = res_data.data;
                        if (this.settingData.s3_file_url) {
                            this.environment.IMAGE_BASE_URL = this.settingData.s3_file_url;
                        }
                        this.imageBaseReady$.next(true);
                    }
                });
        });
    }

    toastrWithTitle(msg: string, title: string, type: `${ToastKind}` | '' = ''): void {
        if (type === ToastKind.Error) {
            this.toastr.error(msg, title, { disableTimeOut: true });
        } else if (type === ToastKind.Info) {
            this.toastr.info(msg, title, { disableTimeOut: true });
        } else if (type === ToastKind.Warning) {
            this.toastr.warning(msg, title, { disableTimeOut: true });
        } else {
            this.toastr.success(msg, title);
        }
    }

    getDeviceToken(): string | false {
        if (!this.getLocalStorage('deviceToken')) {
            this.setLocalStorage('deviceToken', String(Math.round(+new Date(this.currentTime()) / 1000)));
        }
        return this.getLocalStorage('deviceToken');
    }

    /**
   * Every sign-in screen calls this on arrival: a user who is already signed in belongs in their
   * partner portal; one who has not chosen a partner yet goes back to the sign-in landing.
   */
    checkAlredyLogin(currentScreen: any = ''): void {
        if (this.getUserId()) {
            if (this.getLocalStorage('hospitalId')) {
                this.router.navigate(['/partner']);
            } else if (currentScreen != 'select-entity') {
                this.router.navigate(['/partner-signin']);
            }
        }
    }

    /** The headers every authenticated request carries. */
    private requestHeaders(userObj: any): HttpHeaders {
        let headers: any = {};
        if ((this.getUserId() && this.getUserId() != '') || userObj) {
            headers = {
                userid: userObj ? userObj._id : this.getUserId(),
                currentdate: JSON.stringify(Number(this.currentTime())),
                timezone: new window.Intl.DateTimeFormat().resolvedOptions().timeZone,
                token: userObj ? userObj.token : this.getAuthToken(),
                mac: this.getDeviceToken()
            };
        }
        if (this.getLocalStorage('hospitalId')) {
            headers.hospitalid = this.getLocalStorage('hospitalId');
        }
        headers.type = 'heroes';
        headers.devicetype = WEB_DEVICE_TYPE;
        return new HttpHeaders(headers);
    }

    http_post_method_requester(api_name: any, parameter: any, success_alert: any, false_alert: any, loader_start: any, loader_stop: any, response_data: any, userObj: any = null, error_callback: (() => void) | null = null): void {

        if (loader_start) {
            this.myLoading = true;
        }
        const httpOptions = this.requestHeaders(userObj);
        this.incrementAPICallCount();
        this.http.post(this.environment.BASE_URL + api_name, parameter, { headers: httpOptions })
            .subscribe((res) => {
                this.decrementAPICallCount();
                if (loader_stop) {
                    this.myLoading = false;
                }
                const res_data = JSON.parse(JSON.stringify(res));

                if (res_data.success) {
                    if (res_data.messageCode && success_alert) {
                        this.toastr.success(this.trans.instant(`code_${res_data.messageCode}`));
                    } else {
                        if (res_data.message && success_alert) {
                            this.toastr.success(res_data.message);
                        }
                    }
                    response_data(res_data);
                } else {
                    if (res_data.messageCode && false_alert) {
                        if ((res_data.messageCode == HttpMessageCode.EcosystemAccessDenied) || (res_data.messageCode == HttpMessageCode.AccessDenied)) {
                            this.toastr.error(res_data.message);
                        } else {
                            this.toastr.error(this.trans.instant(`code_${res_data.messageCode}`));
                        }
                    } else {
                        if (res_data.message && false_alert) {
                            this.toastr.error(res_data.message);
                        }
                    }
                    response_data(res_data);
                }
            },
            response => {
                this.myLoading = false;
                // Notify the caller on EVERY HTTP error status (0/401/403/500/…) so a broken
                // request can never strand a caller-owned loader/skeleton. Opt-in and default-null.
                error_callback?.();
                if (response.status == 0) {
                    if (!navigator.onLine) {
                        this.toastr.warning('Poor Internet Connection');
                    } else {
                        this.toastr.warning('Something went wrong');
                    }
                    return;
                }
                // A 401 no longer ends the session here. Only messageCode 638 does,
                // and ApiErrorInterceptor owns that — see SessionService.
                if (response.status == 402) {
                    this.handleNotLinked(response, response_data, 5000);
                } else if (response.status == 601) {
                    this.toastr.error(response.error.message);
                    response_data({
                        isSuccess: false
                    });
                }
            });

    }
    http_get_method_requester(api_name: any, parameter: any, success_alert: any, false_alert: any, loader_start: any, loader_stop: any, response_data: any, userObj: any = null, error_callback: (() => void) | null = null): void {
        if (loader_start) {
            this.myLoading = true;
        }
        const httpOptions = this.requestHeaders(userObj);
        this.incrementAPICallCount();
        this.http.get(this.environment.BASE_URL + api_name + parameter, { headers: httpOptions })
            .subscribe((res) => {
                this.decrementAPICallCount();
                const res_data = JSON.parse(JSON.stringify(res));
                if (loader_stop) {
                    this.myLoading = false;
                }

                if (res_data.success) {
                    response_data(res_data);
                } else {
                    if (res_data.messageCode && false_alert) {
                        this.toastr.error(this.trans.instant(`code_${res_data.messageCode}`));
                    } else {
                        if (res_data.message && false_alert) {
                            this.toastr.error(res_data.message);
                        }
                    }
                    response_data(res_data);
                }
            },
            response => {
                // Notify the caller on EVERY HTTP error status so a broken request can never strand a
                // caller-owned loader/skeleton. Opt-in and default-null.
                error_callback?.();
                if (response.status == 0) {
                    if (!navigator.onLine) {
                        this.toastr.warning('Poor Internet Connection');
                    } else {
                        this.toastr.warning('Something went wrong');
                    }
                }
                this.myLoading = false;
                // A 401 no longer ends the session here. Only messageCode 638 does,
                // and ApiErrorInterceptor owns that — see SessionService.
                if (response.status == 402) {
                    this.handleNotLinked(response, response_data, 10000);
                } else if (response.status == 601) {
                    this.toastr.error(response.error.message);
                    response_data({
                        isSuccess: false
                    });
                }
            });
    }

    /** 402 means the account is not linked to a partner — an onboarding state, not a dead session. */
    private handleNotLinked(response: any, response_data: any, reenableAfterMs: number): void {
        if (!this.errorDisabled) {
            this.toastr.error(response.error.message);
        }
        this.router.navigate(['/partner-signin']);
        this.errorDisabled = true;
        response_data(response.error);
        timer(reenableAfterMs).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.errorDisabled = false;
        });
    }

    // logout() and logoutAll() were removed. Ending a session is owned entirely
    // by SessionService.endSession(SessionEndReason.*) — see libs/core/services.
    setUserInfo = (userId: any = '', userData: any = [], authtoken: any = ''): void => {
        this.setLocalStorage('userId', userId);
        const setuserData = (this.getAllUserData() && this.getAllUserData().length > 0) ? this.getAllUserData() : [];
        if (setuserData.length > 0) {
            const index = setuserData.findIndex((x: any) => x._id == userData._id);
            if (index == -1) {
                setuserData.push(userData);
            } else {
                setuserData[index] = userData;
            }
        } else {

            setuserData.push(userData);
        }
        this.setLocalStorage('userData', JSON.stringify(setuserData));
        this.setLocalStorage('authToken', authtoken);
        // Credentials are live again — release the teardown latch so unsaved-changes
        // guards start protecting forms once more.
        this.sessionService.startSession();
        this.userDataUpdated$.next();
    };
    setEntityData(entityDetails: any = []): void {
    // Callers build this list with concat()/push() on values that can be non-arrays,
    // so drop anything that is not a real entity before it reaches localStorage.
        const entities: any[] = Array.isArray(entityDetails) ? entityDetails.filter((entity: any) => entity && entity._id) : [];
        this.setLocalStorage('entityDetails', JSON.stringify(entities));
    }
    setUserData(userData: any = []): void {
        const setuserData = userData;
        this.setLocalStorage('userData', JSON.stringify(setuserData));
    }
    getUserId = (): string | false => {

        const data: any = this.getLocalStorage('userId');
        return data;
    };
    setLocalStorage(key: any, value: any): void {
        const encKey: any = btoa(unescape(encodeURIComponent(key)));
        const encValue: any = btoa(unescape(encodeURIComponent(value)));
        localStorage.setItem(encKey, encValue);
    }
    clearLocalStorage(key: any): void {
        localStorage.removeItem(btoa(unescape(encodeURIComponent(key))));
    }
    getLocalStorage(key: any): string | false {
        const encKey: any = btoa(unescape(encodeURIComponent(key)));
        const data: any = localStorage.getItem(encKey);
        if (!data) {
            return false;
        }
        try {
            return decodeURIComponent(escape(atob(data)));
        } catch (e) {
            return atob(data);
        }
    }
    getUserData = (single: any = true): StoredProfile | StoredProfile[] => {
        let data: any = this.getLocalStorage('userData');
        data = data ? JSON.parse(data) : [];
        if (this.getUserId() && single) {
            const index = data.findIndex((x: any) => x._id == this.getUserId());
            return data[index];
        } else {
            return data;
        }
    };

    getAllUserData = (): StoredProfile[] => {
        let data: any = this.getLocalStorage('userData');
        data = data ? JSON.parse(data) : [];
        return data;
    };
    getAuthToken = (): string | false => {
        const authToken: any = this.getLocalStorage('authToken');
        return authToken;
    };

    /**
   * The cropper has the picture. It used to clear four file inputs found by id across the whole document (the hospital system's
   * screens); no screen in this app has them, and the page image field clears its own input (review MR !1 S17).
   */
    imageLoaded(): void {
        this.cropValue.showCropper = true;
    }

    cropperReady(sourceImageDimensions: any): void {
        if (sourceImageDimensions) {
            this.isLoadImage = true;
        } else {
            this.isLoadImage = false;
        }
    }

    rotateLeft(): void {
        this.cropValue.canvasRotation--;
        this.flipAfterRotate();
    }

    rotateRight(): void {
        this.cropValue.canvasRotation++;
        this.flipAfterRotate();
    }

    private flipAfterRotate(): void {
        const flippedH = this.cropValue.transform.flipH;
        const flippedV = this.cropValue.transform.flipV;
        this.cropValue.transform = {
            ...this.cropValue.transform,
            flipH: flippedV,
            flipV: flippedH
        };
    }

    flipHorizontal(): void {
        this.cropValue.transform = {
            ...this.cropValue.transform,
            flipH: !this.cropValue.transform.flipH
        };
    }

    zoomOut(): void {
        this.cropValue.scale -= .1;
        this.cropValue.transform = {
            ...this.cropValue.transform,
            scale: this.cropValue.scale
        };
    }

    zoomIn(): void {
        this.cropValue.scale += .1;
        this.cropValue.transform = {
            ...this.cropValue.transform,
            scale: this.cropValue.scale
        };
    }

    errorImg(event: any, img: any, type: any = 0): void {
        event.target.src = this.defaultPhoto(img, type);
    }
    defaultPhoto(
        img: any,
        type: PhotoType = PhotoType.Hospital,
        isCheckDoctor = false,
        isDoctor = false
    ): string {
        if (img != '' && img != null) {
            if (img.includes('base64')) {
                return img;
            } else {
                return joinPublicUrl(this.environment.IMAGE_BASE_URL, img);
            }
        }

        if (isCheckDoctor) {
            return isDoctor
                ? 'assets/images/avatarDoctor.svg'
                : 'assets/images/avatarPatient.svg';
        }

        switch (type) {
            case PhotoType.Hospital:
                return 'assets/images/avatarHospital.svg';
            case PhotoType.Patient:
                return 'assets/images/avatarPatient.svg';
            case PhotoType.Doctor:
                return 'assets/images/avatarDoctor.svg';
            case PhotoType.Lab:
                return 'assets/images/avatarLab.svg';
            case PhotoType.Radiology:
                return 'assets/images/avatarRadio.svg';
            case PhotoType.MedicalStore:
                return 'assets/images/avatarMedicalStore.svg';
            case PhotoType.HospitalIcon:
                return 'assets/images/HospitalIcon.svg';
            case PhotoType.LifemaanBot:
                return 'assets/images/lifemaanBot.png';
            default:
                return 'assets/images/avatarHospital.svg'; // fallback
        }
    }

    incrementAPICallCount(): void {
        this.activeRequests++;
    }
    decrementAPICallCount(): void {
        if (this.activeRequests > 0) {
            this.activeRequests--;
        }
    }

}
